import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useSearchParams } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Separator } from "../components/ui/separator";
import { Switch } from "../components/ui/switch";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  Settings,
  User,
  Bell,
  Shield,
  Moon,
  CreditCard,
  Check,
  AlertTriangle,
  Clock,
  Loader2,
  Trash2,
  Monitor,
  Plus,
  ExternalLink,
  Apple,
  Mail,
} from "lucide-react";
import { toast } from "sonner";
import {
  getSubscriptionTiers,
  getSubscriptionStatus,
  createCheckout,
  getApiKeyStatus,
  saveStripeKey as saveStripeKeyApi,
  saveHibpKey as saveHibpKeyApi,
  createScheduledReport,
  getAgentTokens,
  createAgentToken,
  revokeAgentToken,
  getCheckoutStatus,
  get2FAStatus,
  setup2FA,
} from "../lib/api";

export const SettingsPage = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [processingCheckout, setProcessingCheckout] = useState(false);

  // Agent token state
  const [agentTokens, setAgentTokens] = useState([]);
  const [loadingTokens, setLoadingTokens] = useState(false);
  const [creatingToken, setCreatingToken] = useState(false);
  const [newTokenName, setNewTokenName] = useState("");

  // API Keys state
  const [stripeSecretKey, setStripeSecretKey] = useState("");
  const [hibpApiKey, setHibpApiKey] = useState("");
  const [savingStripeKey, setSavingStripeKey] = useState(false);
  const [savingHibpKey, setSavingHibpKey] = useState(false);
  const [apiKeyStatus, setApiKeyStatus] = useState({
    stripe: "test",
    hibp: "not_configured",
  });
  const [tiers, setTiers] = useState([]);
  const [selectedTier, setSelectedTier] = useState("essential");

  useEffect(() => {
    fetchSubscription();
    fetchAgentTokens();
    fetchApiKeyStatus();
    fetchTiers();
    // Check if returning from Stripe
    const sessionId = searchParams.get("session_id");
    const paymentStatus = searchParams.get("payment");
    if (sessionId && paymentStatus === "success") {
      pollPaymentStatus(sessionId);
    } else if (paymentStatus === "cancelled") {
      toast.error("Payment was cancelled");
    }
  }, [searchParams]);

  const fetchTiers = async () => {
    try {
      const response = await getSubscriptionTiers();
      setTiers(response.data.tiers || []);
    } catch (err) {
      console.error("Error fetching tiers:", err);
      toast.error("Failed to load subscription tiers");
    }
  };

  const fetchApiKeyStatus = async () => {
    try {
      const response = await getApiKeyStatus();
      setApiKeyStatus(response.data);
    } catch (err) {
      console.error("Error fetching API key status:", err);
      toast.error("Failed to load API key status");
    }
  };

  const saveStripeKey = async () => {
    if (
      !stripeSecretKey.startsWith("sk_live_") &&
      !stripeSecretKey.startsWith("sk_test_")
    ) {
      toast.error(
        "Invalid Stripe secret key format. Key should start with sk_live_ or sk_test_",
      );
      return;
    }
    setSavingStripeKey(true);
    try {
      await saveStripeKeyApi(stripeSecretKey);
      toast.success("Stripe API key saved successfully!");
      setStripeSecretKey("");
      fetchApiKeyStatus();
    } catch (err) {
      toast.error("Failed to save Stripe API key");
    } finally {
      setSavingStripeKey(false);
    }
  };

  const saveHibpKey = async () => {
    if (!hibpApiKey.trim()) {
      toast.error("Please enter a valid HIBP API key");
      return;
    }
    setSavingHibpKey(true);
    try {
      await saveHibpKeyApi(hibpApiKey);
      toast.success("HIBP API key saved successfully!");
      setHibpApiKey("");
      fetchApiKeyStatus();
    } catch (err) {
      toast.error("Failed to save HIBP API key");
    } finally {
      setSavingHibpKey(false);
    }
  };

  const createScheduledReportHandler = async (name, frequency, reportType) => {
    try {
      await createScheduledReport({
        name,
        frequency,
        report_type: reportType,
        recipients: [user?.email],
        enabled: true,
      });
      toast.success(`${name} scheduled! You'll receive it ${frequency}.`);
    } catch (err) {
      toast.error("Failed to create scheduled report");
    }
  };

  const fetchAgentTokens = async () => {
    setLoadingTokens(true);
    try {
      const response = await getAgentTokens();
      setAgentTokens(response.data.filter((t) => t.is_active));
    } catch (err) {
      console.error("Error fetching agent tokens:", err);
      toast.error("Failed to load agent tokens");
    } finally {
      setLoadingTokens(false);
    }
  };

  const createAgentTokenHandler = async () => {
    setCreatingToken(true);
    try {
      const response = await createAgentToken({
        name: newTokenName || "Default Agent Token",
      });
      setAgentTokens([...agentTokens, response.data]);
      setNewTokenName("");
      toast.success("Agent token created successfully!");
    } catch (err) {
      toast.error("Failed to create agent token");
    } finally {
      setCreatingToken(false);
    }
  };

  const revokeAgentTokenHandler = async (tokenId) => {
    try {
      await revokeAgentToken(tokenId);
      setAgentTokens(agentTokens.filter((t) => t.id !== tokenId));
      toast.success("Token revoked");
    } catch (err) {
      toast.error("Failed to revoke token");
    }
  };

  const downloadWindowsInstaller = (token) => {
    window.open(
      "https://github.com/bcarpinello/new-app/releases/latest/download/securesight-agent-windows-x64.exe",
      "_blank",
    );
  };

  const downloadMacInstaller = (token) => {
    window.open(
      "https://github.com/bcarpinello/new-app/releases/latest/download/securesight-agent-macos-arm64.pkg",
      "_blank",
    );
  };

  const downloadLinuxInstaller = (token) => {
    window.open(
      "https://github.com/bcarpinello/new-app/releases/latest/download/securesight-agent-linux-x64.deb",
      "_blank",
    );
  };

  const fetchSubscription = async () => {
    try {
      const response = await getSubscriptionStatus();
      setSubscription(response.data);
    } catch (err) {
      console.error("Error fetching subscription:", err);
      toast.error("Failed to load subscription status");
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error("Payment status check timed out. Please refresh the page.");
      setCheckingPayment(false);
      return;
    }

    setCheckingPayment(true);

    try {
      const response = await getCheckoutStatus(sessionId);

      if (response.data.payment_status === "paid") {
        toast.success("Payment successful! Your subscription is now active.");
        fetchSubscription();
        setCheckingPayment(false);
        // Clear URL params
        window.history.replaceState({}, "", "/settings");
        return;
      } else if (response.data.status === "expired") {
        toast.error("Payment session expired. Please try again.");
        setCheckingPayment(false);
        return;
      }

      // Continue polling
      setTimeout(
        () => pollPaymentStatus(sessionId, attempts + 1),
        pollInterval,
      );
    } catch (error) {
      console.error("Error checking payment status:", error);
      toast.error("Error verifying payment. Retrying...");
      setTimeout(
        () => pollPaymentStatus(sessionId, attempts + 1),
        pollInterval,
      );
    }
  };

  const handleCheckout = async (tierId) => {
    setProcessingCheckout(true);
    try {
      const response = await createCheckout({
        tier: tierId || selectedTier,
        origin_url: window.location.origin,
      });

      // Redirect to Stripe
      window.location.href = response.data.url;
    } catch (err) {
      toast.error("Failed to create checkout session");
      setProcessingCheckout(false);
    }
  };

  const getInitials = (name) => {
    return (
      name
        ?.split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase() || "U"
    );
  };

  const getTrialDaysRemaining = () => {
    if (!subscription?.trial_ends_at) return 0;
    const trialEnds = new Date(subscription.trial_ends_at);
    const now = new Date();
    const diffMs = trialEnds - now;
    return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const trialDays = getTrialDaysRemaining();
  const isTrialExpired = subscription?.status === "expired";
  const isActive = subscription?.status === "active";
  const isTrial = subscription?.status === "trial";

  return (
    <div className="space-y-6 max-w-4xl" data-testid="settings-page">
      {/* Page Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground">
          Settings
        </h1>
        <p className="text-muted-foreground mt-1">
          Manage your account and subscription
        </p>
      </div>

      {/* Payment Checking Overlay */}
      {checkingPayment && (
        <Card className="border-blue-300 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
              <span className="text-blue-800 font-medium">
                Verifying payment... Please wait.
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Subscription Status */}
      <Card
        className={`border-border shadow-sm ${isTrialExpired ? "border-red-300 bg-red-50" : isTrial ? "border-amber-300 bg-amber-50" : isActive ? "border-green-300 bg-green-50" : ""}`}
      >
        <CardHeader>
          <CardTitle className="font-heading text-lg font-semibold flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            Subscription
          </CardTitle>
          <CardDescription>
            Manage your SecureSight subscription
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Current Status */}
          <div className="flex items-center justify-between p-4 bg-white rounded-lg border">
            <div>
              <p className="text-sm text-muted-foreground">Current Plan</p>
              <p className="font-heading text-xl font-bold">
                {subscription?.tier_name || "No Plan"}
              </p>
              {subscription?.features && (
                <div className="flex gap-2 mt-1">
                  {subscription.features.includes_ai && (
                    <Badge variant="secondary" className="text-xs">
                      AI Enabled
                    </Badge>
                  )}
                  {subscription.features.includes_autofix && (
                    <Badge variant="secondary" className="text-xs">
                      Auto-Fix
                    </Badge>
                  )}
                </div>
              )}
            </div>
            <Badge
              className={`text-sm px-3 py-1 ${
                isActive
                  ? "bg-green-100 text-green-800"
                  : isTrial
                    ? "bg-amber-100 text-amber-800"
                    : "bg-red-100 text-red-800"
              }`}
            >
              {isActive ? "Active" : isTrial ? "Trial" : "Expired"}
            </Badge>
          </div>

          {/* Trial Warning */}
          {isTrial && (
            <div className="flex items-start gap-3 p-4 bg-amber-100 rounded-lg border border-amber-200">
              <Clock className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">Trial Period</p>
                <p className="text-sm text-amber-700">
                  {trialDays > 0
                    ? `You have ${trialDays} days remaining in your free trial.`
                    : "Your trial has expired. Subscribe to continue using SecureSight."}
                </p>
              </div>
            </div>
          )}

          {/* Expired Warning */}
          {isTrialExpired && (
            <div className="flex items-start gap-3 p-4 bg-red-100 rounded-lg border border-red-200">
              <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
              <div>
                <p className="font-medium text-red-800">Trial Expired</p>
                <p className="text-sm text-red-700">
                  Your trial has ended. Subscribe now to continue monitoring
                  your devices.
                </p>
              </div>
            </div>
          )}

          {/* Active Subscription Info */}
          {isActive && (
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-white rounded-lg border">
                <p className="text-sm text-muted-foreground">Monthly Price</p>
                <p className="font-heading text-2xl font-bold">
                  ${subscription?.price}/mo
                </p>
              </div>
              <div className="p-3 bg-white rounded-lg border">
                <p className="text-sm text-muted-foreground">
                  Next Billing Date
                </p>
                <p className="font-medium">
                  {formatDate(subscription?.current_period_end)}
                </p>
              </div>
            </div>
          )}

          <Separator />

          {/* Pricing Tiers */}
          {(!isActive || isTrial || isTrialExpired) && (
            <div className="space-y-4">
              <h3 className="font-semibold">Choose Your Plan</h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Tier 1 - Essential */}
                <Card
                  className={`cursor-pointer transition-all hover:shadow-lg ${selectedTier === "essential" ? "ring-2 ring-slate-500 dark:ring-slate-300 border-2 border-slate-500 dark:border-slate-300 shadow-md" : ""}`}
                  onClick={() => setSelectedTier("essential")}
                >
                  <CardContent className="p-5">
                    <div className="text-center mb-4">
                      <Badge
                        variant="outline"
                        className="mb-2 bg-green-50 text-green-700 border-green-200"
                      >
                        TIER 1
                      </Badge>
                      <p className="font-bold text-lg text-foreground">Essential Visibility</p>
                      <p className="text-3xl font-bold mt-2 text-foreground">
                        $149
                        <span className="text-sm font-normal text-muted-foreground">
                          /mo
                        </span>
                      </p>
                    </div>
                    <p className="text-sm text-muted-foreground text-center mb-4 italic">
                      "Know what's happening with your computers."
                    </p>
                    <ul className="space-y-2 text-sm text-foreground">
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-600 dark:text-green-400" /> Device
                        inventory
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-600 dark:text-green-400" /> Basic
                        health monitoring
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-600 dark:text-green-400" />{" "}
                        Online/offline status
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-600 dark:text-green-400" /> Manual
                        alerts
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-600 dark:text-green-400" /> Basic
                        reporting
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-600 dark:text-green-400" /> Password
                        breach checks
                      </li>
                    </ul>
                    <Button
                      className="w-full mt-4"
                      variant={
                        selectedTier === "essential" ? "default" : "outline"
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCheckout("essential");
                      }}
                      disabled={processingCheckout}
                    >
                      {selectedTier === "essential" && (
                        <Check className="w-4 h-4 mr-2" />
                      )}
                      Select Essential
                    </Button>
                  </CardContent>
                </Card>

                {/* Tier 2 - AI Intelligence */}
                <Card
                  className={`cursor-pointer transition-all hover:shadow-lg ${selectedTier === "intelligence" ? "ring-2 ring-slate-500 dark:ring-slate-300 border-2 border-slate-500 dark:border-slate-300 shadow-md" : ""}`}
                  onClick={() => setSelectedTier("intelligence")}
                >
                  <CardContent className="p-5">
                    <div className="text-center mb-4">
                      <Badge className="mb-2 bg-amber-100 text-amber-800 border-amber-300">
                        MOST POPULAR
                      </Badge>
                      <p className="font-bold text-lg text-foreground">AI Intelligence</p>
                      <p className="text-3xl font-bold mt-2 text-foreground">
                        $299
                        <span className="text-sm font-normal text-muted-foreground">
                          /mo
                        </span>
                      </p>
                    </div>
                    <p className="text-sm text-muted-foreground text-center mb-4 italic">
                      Everything in Essential +
                    </p>
                    <ul className="space-y-2 text-sm text-foreground">
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-600 dark:text-green-400" /> AI disk
                        scoring for devices
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-600 dark:text-green-400" /> Security
                        summaries
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-600 dark:text-green-400" /> Anomaly
                        detection
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-600 dark:text-green-400" /> Compliance
                        reports
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-600 dark:text-green-400" /> Active
                        failure warnings
                      </li>
                    </ul>
                    <Button
                      className="w-full mt-4"
                      variant={
                        selectedTier === "intelligence" ? "default" : "outline"
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCheckout("intelligence");
                      }}
                      disabled={processingCheckout}
                    >
                      {selectedTier === "intelligence" && (
                        <Check className="w-4 h-4 mr-2" />
                      )}
                      Select Intelligence
                    </Button>
                  </CardContent>
                </Card>

                {/* Tier 3 - Autonomous Protection */}
                <Card
                  className={`cursor-pointer transition-all hover:shadow-lg ${selectedTier === "protection" ? "ring-2 ring-slate-500 dark:ring-slate-300 border-2 border-slate-500 dark:border-slate-300 shadow-md" : ""}`}
                  onClick={() => setSelectedTier("protection")}
                >
                  <CardContent className="p-5">
                    <div className="text-center mb-4">
                      <Badge
                        variant="outline"
                        className="mb-2 bg-red-50 text-red-700 border-red-200"
                      >
                        TIER 3
                      </Badge>
                      <p className="font-bold text-lg text-foreground">
                        Autonomous Protection
                      </p>
                      <p className="text-3xl font-bold mt-2 text-foreground">
                        $499
                        <span className="text-sm font-normal text-muted-foreground">
                          /mo
                        </span>
                      </p>
                    </div>
                    <p className="text-sm text-muted-foreground text-center mb-4 italic">
                      Everything in AI Intelligence +
                    </p>
                    <ul className="space-y-2 text-sm text-foreground">
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-600 dark:text-green-400" /> Automated
                        threat response
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-600 dark:text-green-400" /> Auto
                        password enforcement
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-600 dark:text-green-400" />{" "}
                        Autoremediation scripts
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-600 dark:text-green-400" /> Ransomware
                        detection
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-600 dark:text-green-400" /> Priority
                        support
                      </li>
                    </ul>
                    <Button
                      className="w-full mt-4"
                      variant={
                        selectedTier === "protection" ? "default" : "outline"
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCheckout("protection");
                      }}
                      disabled={processingCheckout}
                    >
                      {selectedTier === "protection" && (
                        <Check className="w-4 h-4 mr-2" />
                      )}
                      Select Protection
                    </Button>
                  </CardContent>
                </Card>
              </div>

              {/* Trial Info */}
              <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800">
                  <strong>10-day free trial included</strong> with all plans.
                  Cancel anytime.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Device Agent Section */}
      <Card className="border-border shadow-sm">
        <CardHeader>
          <CardTitle className="font-heading text-lg font-semibold flex items-center gap-2">
            <Monitor className="w-5 h-5" />
            Device Agent
          </CardTitle>
          <CardDescription>
            One dashboard for your business. Install on employee laptops; all
            devices report here.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Download agent - prominent links */}
          <div className="flex flex-wrap items-center gap-3 p-4 bg-muted/40 rounded-lg border">
            <span className="font-medium text-sm text-foreground">Download agent:</span>
            <Button
              size="sm"
              variant="default"
              onClick={() =>
                window.open(
                  "https://github.com/bcarpinello/new-app/releases/latest/download/securesight-agent-windows-x64.exe",
                  "_blank",
                )
              }
              data-testid="download-agent-windows"
            >
              <svg
                className="w-4 h-4 mr-2"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.9-1.801" />
              </svg>
              Windows
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                window.open(
                  "https://github.com/bcarpinello/new-app/releases/latest/download/securesight-agent-macos-arm64.pkg",
                  "_blank",
                )
              }
              data-testid="download-agent-mac"
            >
              <Apple className="w-4 h-4 mr-2" />
              Mac (Apple Silicon)
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                window.open(
                  "https://github.com/bcarpinello/new-app/releases/latest/download/securesight-agent-linux-x64.deb",
                  "_blank",
                )
              }
              data-testid="download-agent-linux"
            >
              Download Linux
            </Button>
          </div>

          {/* Instructions */}
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h4 className="font-semibold text-blue-800 mb-2">
              One token, all your laptops
            </h4>
            <p className="text-sm text-blue-700 mb-2">
              Create one install token and share it with employees. Each
              employee installs the agent on their laptop and enters this token
              (no password needed). All 5 laptops will appear on your dashboard.
            </p>
            <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
              <li>Create an agent token below (e.g. &quot;Company laptops&quot;)</li>
              <li>Copy the install token and send it to your team with the installer</li>
              <li>Employees run the installer and paste the token when prompted</li>
              <li>Download the Windows or Mac installer from the token card</li>
              <li>Devices show up in your dashboard within a minute</li>
            </ol>
          </div>

          {/* Create New Token */}
          <div className="space-y-3">
            <Label>Create New Agent Token</Label>
            <div className="flex gap-2">
              <Input
                placeholder="e.g. Company laptops"
                value={newTokenName}
                onChange={(e) => setNewTokenName(e.target.value)}
                className="flex-1"
                data-testid="agent-token-name-input"
              />
              <Button
                onClick={createAgentTokenHandler}
                disabled={creatingToken}
                data-testid="create-agent-token-btn"
              >
                {creatingToken ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-1" />
                    Create
                  </>
                )}
              </Button>
            </div>
          </div>

          <Separator />

          {/* Existing Tokens */}
          <div className="space-y-3">
            <Label>Your Agent Tokens</Label>
            {loadingTokens ? (
              <div className="flex items-center justify-center p-4">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            ) : agentTokens.length === 0 ? (
              <div className="text-center p-6 bg-muted/30 rounded-lg border-2 border-dashed">
                <Monitor className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">
                  No agent tokens yet. Create one above to get started.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {agentTokens.map((token) => (
                  <div
                    key={token.id}
                    className="p-4 bg-white rounded-lg border"
                    data-testid={`agent-token-${token.id}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{token.name}</p>
                        <p className="text-xs text-muted-foreground">
                          Created{" "}
                          {new Date(token.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => revokeAgentTokenHandler(token.id)}
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        title="Revoke token"
                        data-testid={`revoke-token-${token.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          navigator.clipboard.writeText(token.token);
                          toast.success("Install token copied. Share with employees.");
                        }}
                        data-testid={`copy-token-${token.id}`}
                      >
                        Copy install token
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => downloadWindowsInstaller(token.token)}
                        data-testid={`download-windows-${token.id}`}
                      >
                        <svg
                          className="w-4 h-4 mr-2"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.9-1.801" />
                        </svg>
                        Windows
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => downloadMacInstaller(token.token)}
                        data-testid={`download-mac-${token.id}`}
                      >
                        <Apple className="w-4 h-4 mr-2" />
                        Mac
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Employees install with this token (no password). All devices appear on your dashboard.
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Requirements Note */}
          <div className="text-xs text-muted-foreground bg-muted/30 p-3 rounded-lg">
            <p className="font-medium mb-1">How it works:</p>
            <ul className="list-disc list-inside space-y-0.5">
              <li>Share the install token with employees (they don’t need your password)</li>
              <li>Windows: run installer as Administrator, then <code className="bg-muted px-1 rounded">securesight-agent.exe install --agent-token YOUR_TOKEN --agent-api YOUR_API_URL</code></li>
              <li>Mac: <code className="bg-muted px-1 rounded">securesight-agent install --agent-token YOUR_TOKEN --agent-api YOUR_API_URL</code></li>
              <li>All devices appear on your dashboard; agent sends health data every 10 seconds</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Profile Section */}
      <Card className="border-border shadow-sm">
        <CardHeader>
          <CardTitle className="font-heading text-lg font-semibold flex items-center gap-2">
            <User className="w-5 h-5" />
            Profile
          </CardTitle>
          <CardDescription>Your account information</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center gap-4">
            <Avatar className="h-16 w-16">
              <AvatarFallback className="bg-primary text-primary-foreground text-xl">
                {getInitials(user?.name)}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="font-semibold text-lg">{user?.name}</p>
              <p className="text-muted-foreground">{user?.email}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* API Keys Section */}
      <Card className="border-border shadow-sm">
        <CardHeader>
          <CardTitle className="font-heading text-lg font-semibold flex items-center gap-2">
            <Shield className="w-5 h-5" />
            API Keys
          </CardTitle>
          <CardDescription>
            Configure third-party integrations and API keys
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Stripe API Keys */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium">Stripe Payment Keys</h4>
                <p className="text-sm text-muted-foreground">
                  Enter your Stripe API keys to enable live payments
                </p>
              </div>
              <Badge
                className={
                  apiKeyStatus.stripe === "live"
                    ? "bg-green-100 text-green-800"
                    : "bg-amber-100 text-amber-800"
                }
                data-testid="stripe-mode-badge"
              >
                {apiKeyStatus.stripe === "live" ? "Live Mode" : "Test Mode"}
              </Badge>
            </div>

            {apiKeyStatus.stripe !== "live" && (
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium">Currently using test keys</p>
                    <p>
                      To accept real payments, add your live Stripe API keys
                      below.{" "}
                      <a
                        href="https://dashboard.stripe.com/apikeys"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline hover:no-underline"
                      >
                        Get your API keys from Stripe Dashboard
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            )}

            {apiKeyStatus.stripe === "live" && (
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-600 mt-0.5" />
                  <div className="text-sm text-green-800">
                    <p className="font-medium">Live payments enabled</p>
                    <p>
                      Your Stripe account is connected and ready to accept real
                      payments.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="stripe-secret">Secret Key</Label>
                <div className="flex gap-2">
                  <Input
                    id="stripe-secret"
                    type="password"
                    placeholder="sk_live_..."
                    value={stripeSecretKey}
                    onChange={(e) => setStripeSecretKey(e.target.value)}
                    data-testid="stripe-secret-key-input"
                    className="font-mono text-sm"
                  />
                  <Button
                    variant="outline"
                    onClick={saveStripeKey}
                    disabled={savingStripeKey || !stripeSecretKey}
                    data-testid="save-stripe-key-btn"
                  >
                    {savingStripeKey ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      "Save"
                    )}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Your secret key is stored securely and never exposed to the
                  frontend
                </p>
              </div>
            </div>
          </div>

          <Separator />

          {/* HIBP API Key */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium">Have I Been Pwned API Key</h4>
                <p className="text-sm text-muted-foreground">
                  Required for checking email addresses against breach databases
                </p>
              </div>
              <Badge
                className={
                  apiKeyStatus.hibp === "configured"
                    ? "bg-green-100 text-green-800"
                    : "bg-gray-100 text-gray-700"
                }
                data-testid="hibp-status-badge"
              >
                {apiKeyStatus.hibp === "configured"
                  ? "Configured"
                  : "Not Configured"}
              </Badge>
            </div>

            {apiKeyStatus.hibp !== "configured" ? (
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-start gap-2">
                  <Shield className="w-4 h-4 text-blue-600 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium">Why do I need this?</p>
                    <p>
                      The HIBP API lets you check if email addresses have
                      appeared in known data breaches. Password checking works
                      without a key, but email breach detection requires a paid
                      API key.{" "}
                      <a
                        href="https://haveibeenpwned.com/API/Key"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline hover:no-underline"
                      >
                        Get your HIBP API key
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-600 mt-0.5" />
                  <div className="text-sm text-green-800">
                    <p className="font-medium">Email breach checking enabled</p>
                    <p>
                      You can now scan team members for email breaches from the
                      People page.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="hibp-key">API Key</Label>
              <div className="flex gap-2">
                <Input
                  id="hibp-key"
                  type="password"
                  placeholder="Enter your HIBP API key..."
                  value={hibpApiKey}
                  onChange={(e) => setHibpApiKey(e.target.value)}
                  data-testid="hibp-api-key-input"
                  className="font-mono text-sm"
                />
                <Button
                  variant="outline"
                  onClick={saveHibpKey}
                  disabled={savingHibpKey || !hibpApiKey}
                  data-testid="save-hibp-key-btn"
                >
                  {savingHibpKey ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    "Save"
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                After saving, you can scan all team members for email breaches
                from the People page
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notifications Section */}
      <Card className="border-border shadow-sm">
        <CardHeader>
          <CardTitle className="font-heading text-lg font-semibold flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Notifications
          </CardTitle>
          <CardDescription>
            Configure how you receive notifications
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Email Notifications</Label>
              <p className="text-sm text-muted-foreground">
                Receive alerts via email
              </p>
            </div>
            <Switch defaultChecked data-testid="email-notifications-switch" />
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Critical Alerts</Label>
              <p className="text-sm text-muted-foreground">
                Get notified for critical security events
              </p>
            </div>
            <Switch defaultChecked data-testid="critical-alerts-switch" />
          </div>
        </CardContent>
      </Card>

      {/* Two-Factor Authentication Section */}
      <Card className="border-border shadow-sm">
        <CardHeader>
          <CardTitle className="font-heading text-lg font-semibold flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Two-Factor Authentication
          </CardTitle>
          <CardDescription>
            Add an extra layer of security to your account
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Authenticator App</Label>
              <p className="text-sm text-muted-foreground">
                Use an authenticator app like Google Authenticator or Authy
              </p>
            </div>
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  const response = await get2FAStatus();
                  if (response.data?.enabled) {
                    toast.info(
                      "2FA is already enabled. Contact support to disable it.",
                    );
                  } else {
                    const setupResponse = await setup2FA();
                    // Would show QR code modal here
                    toast.success(
                      "2FA setup initiated. Scan the QR code with your authenticator app.",
                    );
                    // QR code URI available in setupResponse.data.qr_code
                  }
                } catch (err) {
                  toast.error("Failed to setup 2FA");
                }
              }}
              data-testid="setup-2fa-btn"
            >
              Setup 2FA
            </Button>
          </div>
          <div className="p-4 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200 dark:border-blue-800">
            <div className="flex items-start gap-2">
              <Shield className="w-4 h-4 text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-800 dark:text-blue-200">
                <p className="font-medium">Why enable 2FA?</p>
                <p>
                  Two-factor authentication adds an extra layer of security by
                  requiring a code from your phone in addition to your password
                  when logging in.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Scheduled Reports Section */}
      <Card className="border-border shadow-sm">
        <CardHeader>
          <CardTitle className="font-heading text-lg font-semibold flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Scheduled Reports
          </CardTitle>
          <CardDescription>
            Set up automatic security reports delivered to your inbox
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Daily Security Summary</Label>
                <p className="text-sm text-muted-foreground">
                  Receive a daily overview of security score, alerts, and device
                  status
                </p>
              </div>
              <Switch
                data-testid="daily-report-switch"
                onCheckedChange={(checked) => {
                  if (checked) {
                    createScheduledReportHandler(
                      "Daily Security Summary",
                      "daily",
                      "security_summary",
                    );
                  }
                }}
              />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Weekly Compliance Report</Label>
                <p className="text-sm text-muted-foreground">
                  Get a weekly compliance status report every Monday
                </p>
              </div>
              <Switch
                data-testid="weekly-compliance-switch"
                onCheckedChange={(checked) => {
                  if (checked) {
                    createScheduledReportHandler(
                      "Weekly Compliance Report",
                      "weekly",
                      "compliance",
                    );
                  }
                }}
              />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Monthly Device Health Report</Label>
                <p className="text-sm text-muted-foreground">
                  Monthly summary of device health and performance trends
                </p>
              </div>
              <Switch
                data-testid="monthly-health-switch"
                onCheckedChange={(checked) => {
                  if (checked) {
                    createScheduledReportHandler(
                      "Monthly Device Health",
                      "monthly",
                      "device_health",
                    );
                  }
                }}
              />
            </div>
          </div>

          <div className="pt-4 border-t">
            <p className="text-sm text-muted-foreground">
              Reports will be sent to: <strong>{user?.email}</strong>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
